import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { EventBus } from '@/core/EventBus';
import type { UnitFactory } from '@/units/UnitFactory';
import { Owner, UnitData } from '@/ecs/components/UnitComponents';
import { BENCH_SIZE } from '@/utils/constants';

/**
 * Bench events
 */
export const BenchEvents = {
  UNIT_ADDED_TO_BENCH: 'bench:unitAdded',
  UNIT_REMOVED_FROM_BENCH: 'bench:unitRemoved',
  BENCH_FULL: 'bench:full',
} as const;

export interface BenchSlot {
  entityId: string | null;
  playerId: number;
}

/**
 * Manages the bench (reserve units) for both players
 */
export class BenchManager {
  private world: World;
  private eventBus: EventBus;
  private unitFactory: UnitFactory;

  // Bench slots for each player
  private player1Bench: (string | null)[];
  private player2Bench: (string | null)[];

  constructor(world: World, eventBus: EventBus, unitFactory: UnitFactory) {
    this.world = world;
    this.eventBus = eventBus;
    this.unitFactory = unitFactory;

    // Initialize empty benches
    this.player1Bench = new Array(BENCH_SIZE).fill(null);
    this.player2Bench = new Array(BENCH_SIZE).fill(null);
  }

  /**
   * Get bench for a player
   */
  private getBench(playerId: number): (string | null)[] {
    return playerId === 1 ? this.player1Bench : this.player2Bench;
  }

  /**
   * Add a unit to the bench
   */
  addToBench(entity: Entity, playerId: number): number {
    const bench = this.getBench(playerId);
    const emptySlot = bench.findIndex((slot) => slot === null);

    if (emptySlot === -1) {
      this.eventBus.emit(BenchEvents.BENCH_FULL, { playerId });
      return -1;
    }

    // Move unit to bench
    this.unitFactory.moveToBench(entity, emptySlot);
    bench[emptySlot] = entity.id;

    this.eventBus.emit(BenchEvents.UNIT_ADDED_TO_BENCH, {
      entityId: entity.id,
      playerId,
      slot: emptySlot,
    });

    return emptySlot;
  }

  /**
   * Remove a unit from the bench
   */
  removeFromBench(entityId: string, playerId: number): boolean {
    const bench = this.getBench(playerId);
    const slotIndex = bench.findIndex((slot) => slot === entityId);

    if (slotIndex === -1) return false;

    bench[slotIndex] = null;

    this.eventBus.emit(BenchEvents.UNIT_REMOVED_FROM_BENCH, {
      entityId,
      playerId,
      slot: slotIndex,
    });

    return true;
  }

  /**
   * Get unit at bench slot
   */
  getUnitAtSlot(playerId: number, slot: number): Entity | null {
    const bench = this.getBench(playerId);
    if (slot < 0 || slot >= bench.length) return null;

    const entityId = bench[slot];
    if (!entityId) return null;

    return this.world.getEntity(entityId) ?? null;
  }

  /**
   * Get all bench units for a player
   */
  getBenchUnits(playerId: number): Entity[] {
    const bench = this.getBench(playerId);
    const units: Entity[] = [];

    for (const entityId of bench) {
      if (entityId) {
        const entity = this.world.getEntity(entityId);
        if (entity && !entity.destroyed) {
          units.push(entity);
        }
      }
    }

    return units;
  }

  /**
   * Get bench state for a player
   */
  getBenchState(playerId: number): BenchSlot[] {
    const bench = this.getBench(playerId);
    return bench.map((entityId) => ({
      entityId,
      playerId,
    }));
  }

  /**
   * Get number of units on bench
   */
  getBenchCount(playerId: number): number {
    return this.getBench(playerId).filter((slot) => slot !== null).length;
  }

  /**
   * Check if bench is full
   */
  isBenchFull(playerId: number): boolean {
    return this.getBenchCount(playerId) >= BENCH_SIZE;
  }

  /**
   * Find first empty bench slot
   */
  getFirstEmptySlot(playerId: number): number {
    const bench = this.getBench(playerId);
    return bench.findIndex((slot) => slot === null);
  }

  /**
   * Swap bench slots
   */
  swapBenchSlots(playerId: number, slot1: number, slot2: number): boolean {
    const bench = this.getBench(playerId);

    if (slot1 < 0 || slot1 >= bench.length) return false;
    if (slot2 < 0 || slot2 >= bench.length) return false;

    const temp = bench[slot1];
    bench[slot1] = bench[slot2];
    bench[slot2] = temp;

    // Update owner component bench slots
    const entity1 = bench[slot1] ? this.world.getEntity(bench[slot1]!) : null;
    const entity2 = bench[slot2] ? this.world.getEntity(bench[slot2]!) : null;

    if (entity1) {
      const owner = entity1.getComponent(Owner);
      if (owner) {
        (owner as { benchSlot: number | null }).benchSlot = slot1;
      }
    }
    if (entity2) {
      const owner = entity2.getComponent(Owner);
      if (owner) {
        (owner as { benchSlot: number | null }).benchSlot = slot2;
      }
    }

    return true;
  }

  /**
   * Clear entire bench for a player (sell/remove all units)
   */
  clearBench(playerId: number): void {
    const bench = this.getBench(playerId);

    for (let i = 0; i < bench.length; i++) {
      if (bench[i]) {
        const entity = this.world.getEntity(bench[i]!);
        if (entity) {
          this.unitFactory.removeUnit(entity);
        }
        bench[i] = null;
      }
    }
  }

  /**
   * Find unit by type on bench (for merging)
   */
  findUnitByType(
    playerId: number,
    unitType: string,
    starLevel: number
  ): Entity[] {
    const benchUnits = this.getBenchUnits(playerId);
    return benchUnits.filter((entity) => {
      const unitData = entity.getComponent(UnitData);
      return unitData && unitData.unitType === unitType && unitData.starLevel === starLevel;
    });
  }

  /**
   * Clean up invalid bench references
   */
  cleanupDestroyedUnits(): void {
    for (const bench of [this.player1Bench, this.player2Bench]) {
      for (let i = 0; i < bench.length; i++) {
        if (bench[i]) {
          const entity = this.world.getEntity(bench[i]!);
          if (!entity || entity.destroyed) {
            bench[i] = null;
          }
        }
      }
    }
  }

  /**
   * Reset benches (for new game)
   */
  reset(): void {
    this.player1Bench = new Array(BENCH_SIZE).fill(null);
    this.player2Bench = new Array(BENCH_SIZE).fill(null);
  }
}
